import { CustomImageType } from "@bared/groq";
import { useRoutes } from "@hooks/useRoutes";
import { type ComponentProps, type ResolvedLink } from "@ts/components";
import {
  BannerSwiperContent,
  type SectionBanner,
  type Styles,
} from "@ts/sanity";
import React, { type FC } from "react";
import { ZodVoidDef } from "zod";

type BannerCarousel = Omit<
  BannerSwiperContent,
  "desktopImage" | "mobileImage" | "textColor"
> & {
  desktopImage: CustomImageType;
  desktopImageAspectRatio: string;
  desktopImagelink?: ResolvedLink;
  desktopVideoUrl?: string;
  mobileImage: CustomImageType;
  mobileImageAspectRatio: string;
  mobileVideoUrl?: string;
  textColor: "primary" | "lightest";
};

export type BannerInputProps = ComponentProps &
  Omit<SectionBanner, "styles"> & {
    styles: Styles[] | null;
    bannerContent?: BannerSwiperContent[];
  };

export type BannerOutputProps = Omit<
  BannerInputProps,
  "imageLink" | "mobileImageLink" | "bannerContent"
> & {
  bannerContent?: BannerCarousel[];
  handleBannerClick?: (url?: string) => void;
  handlePromoClick?: () => ZodVoidDef;
  transparentHeader?: boolean;
};

export const withBanner =
  (Component: FC<BannerOutputProps>) =>
  ({ name = "Banner", bannerContent, ...props }: BannerInputProps) => {
    const { linkResolver } = useRoutes();

    const bannerCarousel = bannerContent?.map((item) => {
      const desktopVideoUrl = item.desktopVideo?.url;
      const mobileVideoUrl = item.mobileVideo?.url;
      const desktopImagelink = linkResolver(item.desktopImage?.imagelink?.[0]);

      return {
        ...item,
        desktopImagelink,
        desktopVideoUrl,
        mobileVideoUrl,
      };
    });

    Component.displayName = name;
    return <Component {...props} bannerContent={bannerCarousel} />;
  };
